import { get } from 'lodash';
import type { ParametricSelector } from 'reselect';

import { subscribe } from '@swf/core/api/subscribe';
import { Vertical } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import { fetchJson } from '@swf/core/utils/api';

const services = {
  [Vertical.Global]: ['cashier', 'notifications', 'crm'],
  [Vertical.Account]: ['cashier', 'notifications', 'crm'],
  [Vertical.Casino]: ['cashier', 'notifications', 'crm', 'casino'],
  [Vertical.Sports]: ['cashier', 'notifications', 'crm']
};

interface SytemMessage {
  body: SystemState;
}

export interface SystemState {
  [key: string]: 'green' | 'amber' | 'red';
}

const reducers = {
  set: (state: SystemState, { body }: { body: SystemState }): SystemState => ({ ...state, ...body })
};

const effects = {
  async subscribe(payload: undefined, { dispatch }: EffectParams) {
    const body = await fetchJson<SystemState>('/api/v1-preview/system/status');
    dispatch.system.set({ body });
    subscribe<SytemMessage>('/topic/system/status').subscribe(dispatch.system.set);
  }
};

interface SystemModel extends ModelConfig<SystemState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const selectors = (slice: Slicer<SystemState>, createSelector: SelectorCreator) => ({
  getStatus() {
    return createSelector(
      slice,
      (state: RootState, system: string) => system,
      (systemState, system) => get(systemState, system, 'green') as string
    );
  },
  isAvailable(this: { getStatus: ParametricSelector<RootState, string | undefined, string> }) {
    return (state: RootState) =>
      services[select.page.getVertical(state)].every(
        (system: string) => this.getStatus(state, system) !== 'red'
      );
  },
  isCMSAvailable(this: { getStatus: ParametricSelector<RootState, string | undefined, string> }) {
    return (state: RootState) =>
      ![Vertical.Casino, Vertical.Global, Vertical.Sports].includes(
        select.page.getVertical(state)
      ) || this.getStatus(state, 'cms') !== 'red';
  },
  isRAMAvailable(this: { getStatus: ParametricSelector<RootState, string | undefined, string> }) {
    return (state: RootState) =>
      ['auth', 'account'].every((service) => this.getStatus(state, service) !== 'red');
  }
});

const system: SystemModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default system;
